import {
  BoxProps,
  Button,
  ChakraAsyncSelect,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useCreateCreditFacilityApi, useSearchBnplRestaurantsApi } from 'apis/bnpl';
import { useSearchSuppliersApi } from 'apis/supplier';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = {
  value: number;
  label: string;
};

interface FormInputs {
  restaurant: TOption | null;
  supplier: TOption | null;
  creditLimit: string;
}

const defaultValues: FormInputs = {
  restaurant: null,
  supplier: null,
  creditLimit: '',
};

export interface ICreateCreditFacilityModalCell extends BoxProps {
  trigger: JSX.Element;
}

export const CreateCreditFacilityModal = ({ trigger }: ICreateCreditFacilityModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate: createFacility, isLoading } = useCreateCreditFacilityApi();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const { loadOptions: loadRestaurantOptions } = useSearchBnplRestaurantsApi();

  const [supplierSearch, setSupplierSearch] = useState('');
  const { data: suppliersData, isLoading: isSuppliersLoading } = useSearchSuppliersApi(supplierSearch, {
    enabled: isOpen && supplierSearch.length >= 2,
  });

  const supplierOptions = useMemo(() => {
    if (!suppliersData?.data) return [];
    return suppliersData.data.map((s) => ({ value: s.id, label: s.name }));
  }, [suppliersData]);

  const onCloseModal = () => {
    onClose();
    reset(defaultValues);
    setSupplierSearch('');
  };

  const onSubmit = (data: FormInputs) => {
    if (!data.restaurant || !data.supplier) return;

    createFacility(
      {
        body: {
          restaurantId: data.restaurant.value,
          supplierId: data.supplier.value,
          creditLimit: parseFloat(data.creditLimit),
        },
      },
      {
        onSuccess: () => {
          onCloseModal();
        },
      }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Credit Facility</ModalHeader>
          <ModalBody>
            <Stack as='form' id='createCreditFacilityForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                control={control}
                name='restaurant'
                rules={{ required: 'Restaurant is required.' }}
                render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel>Restaurant</FormLabel>
                    <ChakraAsyncSelect
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      loadOptions={loadRestaurantOptions}
                      placeholder='Type to search restaurants...'
                      isClearable
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No restaurants found'
                      }
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name='supplier'
                rules={{ required: 'Supplier is required.' }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel>Supplier</FormLabel>
                    <ChakraSelect
                      options={supplierOptions}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      placeholder='Search supplier by name...'
                      isLoading={isSuppliersLoading}
                      onInputChange={(inputValue: string) => {
                        setSupplierSearch(inputValue);
                      }}
                      filterOption={() => true}
                      isClearable
                      noOptionsMessage={() =>
                        supplierSearch.length < 2 ? 'Type at least 2 characters to search' : 'No suppliers found'
                      }
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <FormControl isInvalid={!!errors.creditLimit} isRequired>
                <FormLabel>Credit Limit (AED)</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  min={0.01}
                  placeholder='Enter credit limit'
                  {...register('creditLimit', {
                    required: 'Credit limit is required.',
                    min: { value: 0.01, message: 'Credit limit must be greater than 0.' },
                    validate: (value) => !isNaN(parseFloat(value)) || 'Please enter a valid number.',
                  })}
                />
                <FormErrorMessage>{errors.creditLimit?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='createCreditFacilityForm'
                isLoading={isLoading}>
                Create Facility
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
